import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Anchor, Box, Button, Paper, PaperProps, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCalendar } from '@tabler/icons-react';
import Router from 'next/router';
import Helper from '@/utils/helper';
import SubscriptionContext from '@/utils/subscription-context';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import { getUserFromCookie } from '@/utils/auth';
import { hasSubscribed, isOnboardingEligible } from '@/PageComponents/Onboarding/onboarding-rules';
import { HUBSPOT_DEMO_MEETING_URL } from '@/PageComponents/Onboarding/onboarding-config';
import { useDemoBooking } from '@/PageComponents/Onboarding/useDemoBooking';
import { MeetingBooking } from '@/PageComponents/Onboarding/hubspot-booking';
import BookDemoModal from '@/PageComponents/Onboarding/components/BookDemoModal';
import { ONBOARDING_EVENTS, OnboardingPage, setOnboardingAnalyticsContext, trackOnboarding, useOnboardingPageView } from '@/PageComponents/Onboarding/onboarding-analytics';
import styles from '@/PageComponents/Onboarding/components/BookDemoCard.module.css';

/** Onboarding companies (still on their trial) are offered a demo outside the dashboard too. */
export const useIsOnboardingCompany = (): boolean => {
    const subscriptionContext = useContext<any>(SubscriptionContext);
    const subscriptionInfo = subscriptionContext?.subscriptionInfo || Storage.getCookie(Enums.Cookie.subscriptionInfo);
    return isOnboardingEligible(subscriptionInfo) && !hasSubscribed(subscriptionInfo);
};

const backToSetup = () => Helper.nextRouter(Router.push, '/');

/** Pages the walkthrough card appears on. */
export type WalkthroughCardPage = Exclude<OnboardingPage, 'dashboard'>;

export interface BookDemoCardProps extends PaperProps {
    /** The page it's on: for analytics, and its buttons' ids. */
    page: WalkthroughCardPage;
    /** What the demo helps with on this page, e.g. "Book a free demo and we'll help you add your jobs." */
    description: string;
    /** Once a demo is booked, still offer the way back to the setup steps (just the link). */
    keepBackLink?: boolean;
}

/**
 * "Let us help you with a walkthrough." On pages that take an onboarding company away from its
 * dashboard (create job, imports, integrations): book a demo, or go back to the basic setup.
 * The card shows only while no demo is booked or held, wherever it was booked (see useDemoBooking).
 */
const BookDemoCard: FC<BookDemoCardProps> = ({ page, description, keepBackLink = false, className, ...paperProps }) => {
    const onboarding = useIsOnboardingCompany();
    const { booked, demo, isLoading, setLocalBooking } = useDemoBooking(onboarding);
    const [modalOpen, setModalOpen] = useState(false);
    const user = useMemo(() => getUserFromCookie(), []);

    const onBooked = (booking: MeetingBooking) => {
        setLocalBooking({ startISO: booking.startISO, bookedAtISO: new Date().toISOString() });
        showNotification({ color: 'green', message: 'Your demo is booked — we look forward to meeting you.' });
    };

    const hasDemo = booked || demo.held;
    const location = `walkthrough_card_${page}` as const;
    const ready = onboarding && !isLoading;

    // Onboarding companies' visits to the pages onboarding sends them to, once per visit.
    const demoState = demo.held ? 'held' : booked ? 'booked' : 'none';
    useEffect(() => {
        if (ready) setOnboardingAnalyticsContext({ page, steps_completed: undefined, demo_state: demoState });
    }, [ready, page, demoState]);
    useOnboardingPageView(page, ready);

    if (!ready) return null;

    const onBackToSetup = () => {
        trackOnboarding(ONBOARDING_EVENTS.backToSetupClicked, { location });
        backToSetup();
    };
    const onBookDemo = () => {
        trackOnboarding(ONBOARDING_EVENTS.bookDemoClicked, { location });
        setModalOpen(true);
    };
    const backLink = (
        <Anchor id={`onboarding-back-to-setup-${page.replace(/_/g, '-')}`} component="button" type="button" className={styles.back} onClick={onBackToSetup}>
            Back to basic setup
        </Anchor>
    );

    return (
        <>
            {!hasDemo && (
                <Paper className={`${styles.card} ${className ?? ''}`} data-testid="book-demo-card" {...paperProps}>
                    <div className={styles.tile}><IconCalendar size={22} stroke={1.75} /></div>
                    <div className={styles.copy}>
                        <Text className={styles.title}>Let us help you with a walkthrough.</Text>
                        <Text className={styles.text}>{description}</Text>
                    </div>
                    <div className={styles.actions}>
                        <Button id={`onboarding-book-demo-walkthrough-${page.replace(/_/g, '-')}`} radius="xl" className={styles.button} leftSection={<IconCalendar size={16} />} onClick={onBookDemo}>
                            Book a demo
                        </Button>
                        {backLink}
                    </div>
                </Paper>
            )}
            {hasDemo && keepBackLink && !modalOpen && <Box className={className} {...paperProps}>{backLink}</Box>}
            <BookDemoModal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                onBooked={onBooked}
                location={location}
                meetingUrl={HUBSPOT_DEMO_MEETING_URL}
                user={{ firstName: user?.firstName, lastName: user?.lastName, email: user?.email }}
            />
        </>
    );
};

export default BookDemoCard;
